import { IconSize } from '@ardoq/icons';
import { noop } from 'lodash';
import { SmallGhostButton } from '@ardoq/button';
import { useState, JSX } from 'react';
import { SortIcon, ThButtonWrapper } from './TableUI';
import { HeaderCell } from './types';
import { SortOrder } from '@ardoq/api-types';
import { Maybe } from '@ardoq/common-helpers';

type UseSortableTableOptions = {
  initialSortBy?: string | null;
  initialSortOrder?: SortOrder;
  skipDefaultSortOrder?: boolean;
  onSortChange?: (sortBy: string | null, sortOrder: SortOrder) => void;
};

const getNextSortOrder = (
  sortOrder: SortOrder,
  skipDefaultSortOrder?: boolean
): SortOrder => {
  if (sortOrder === SortOrder.DEFAULT) return SortOrder.ASC;
  if (sortOrder === SortOrder.ASC) return SortOrder.DESC;
  // if DESC
  return skipDefaultSortOrder ? SortOrder.ASC : SortOrder.DEFAULT;
};

export type GetSortableColumnsProps = (columnsSortBy: string) => {
  headerRender: (headerProps: HeaderCell) => JSX.Element;
};

export type UseSortableTableData = {
  sortOrder: SortOrder;
  sortBy: Maybe<string>;
  getOnSortByClick: (clickedSortBy: string) => () => void;
  getSortableColumnProps: GetSortableColumnsProps;
};

export const useSortableTable = ({
  initialSortBy = null,
  initialSortOrder = SortOrder.DEFAULT,
  skipDefaultSortOrder = false,
  onSortChange = noop,
}: UseSortableTableOptions = {}): UseSortableTableData => {
  const [sortBy, setSortBy] = useState<string | null>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const getOnSortByClick = (clickedSortBy: string) => () => {
    const newSortOrder =
      clickedSortBy === sortBy
        ? getNextSortOrder(sortOrder, skipDefaultSortOrder)
        : SortOrder.ASC;
    const newSortBy = newSortOrder === SortOrder.DEFAULT ? null : clickedSortBy;
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    onSortChange(newSortBy, newSortOrder);
  };

  return {
    sortOrder,
    sortBy,
    getOnSortByClick,
    getSortableColumnProps: (columnsSortBy: string) => {
      const renderSortableHeader = ({ title }: HeaderCell) => (
        <>
          {title}
          <ThButtonWrapper $showContentOnHoverOnly={columnsSortBy !== sortBy}>
            <SmallGhostButton onClick={getOnSortByClick(columnsSortBy)}>
              <SortIcon
                order={columnsSortBy === sortBy ? sortOrder : SortOrder.ASC}
                iconSize={IconSize.MEDIUM}
              />
            </SmallGhostButton>
          </ThButtonWrapper>
        </>
      );
      return {
        headerRender: renderSortableHeader,
      };
    },
  };
};
